import {
  Atrule,
  AtrulePrelude,
  Block,
  clone,
  CssLocation,
  CssNode, List,
  ListItem,
  MediaFeature,
  MediaQuery,
  Selector,
  SelectorList,
  WalkContext,
  WhiteSpace,
} from '@sethfowler/css-tree';
import { flatten } from 'lodash';
import { CSSTreeTransform } from './csstreetransforms';
import { createAtrulePrelude, createBlock, createDimension, createMediaAtrule, createMediaQueryList, createNegation, createPseudoClassSelector, createSelectorFromMediaFeature, MediaRewriteSelector, Mutated, mutatedTag, PossiblyMutated } from './utils';


/**
 * This is a map of a media feature string (i.e. MediaFeature#name) to a function that
 * returns a selector that is equivalent to it in playback.
 *
 * Example:
 * Suppose we have @media (pointer: fine) { p { color: red; } }
 * The MediaFeature equivalent to this is something like { name: 'pointer', value: 'fine' }.
 * This rewrite will change the pointer media feature to (min-width: 0), which is always true;
 * and the rules in this block will be prepended with the playback equivalent of (pointer: fine),
 * which is :root[_fs_pointer_="fine"] in this case
 *
 * Other media feature can be rewritten in a similar manner:
 * 1. Detect what media end user is using (via eventwatcher.ts) and record it
 * 2. In playback, mutate the DOM in some way according to the media
 *    (for pointer, the root node is set with a _fs_pointer attribute)
 * 3. Add an entry to the map that generates a selector according to the media feature
 */
const mediaFeaturesToBeReplaced: {
  [mediaFeature: string]: (mediaFeature: MediaFeature) => MediaRewriteSelector;
} = {
  pointer: mf => createSelectorFromMediaFeature(mf, '_fs_pointer'),
  'prefers-color-scheme': mf => createSelectorFromMediaFeature(mf, '_fs_prefers_color_scheme'),
};

export function CSSTreeMediaTransform(): CSSTreeTransform {
  const selectorStack: MediaRewriteSelector[][] = [];
  return {
    enter(
      this: WalkContext,
      node: CssNode,
      item: ListItem<CssNode>,
      list: List<CssNode>,
    ) {
      switch (node.type) {
        case 'Atrule': {
          if (node.name !== 'media' || node.prelude?.type !== 'AtrulePrelude') {
            break;
          }

          const mediaQueries = getMediaQueriesFromPrelude(node.prelude);

          // TODO(eugenechang) currently the types for List#some is incorrect, hence the toArray here
          const needsReplacement = mediaQueries.some(mq => mq.children?.toArray().some(shouldReplaceMediaQueryChild));

          if (mediaQueries.length > 1 && needsReplacement) {
            // if we need replacement and it has more than 1 media query, we need to clone the rules
            // and we will not process them until we enter them again later on
            const clonedRules = mediaQueries.map<Atrule>(mq => {
              const mediaQueryList = createMediaQueryList(node.prelude?.loc, clone(mq));
              const prelude = createAtrulePrelude(node.prelude?.loc, mediaQueryList);
              const block = node.block ? clone(node.block) as PossiblyMutated<Block> : null;
              return createMediaAtrule(prelude, node.loc, block);
            });

            const wrapperRule = createMediaAtrule(null, node.loc, createBlock(node.block?.loc, ...clonedRules));
            // insert our rule after current item, or append if current item is last
            if (!item.next) {
              list.appendData(wrapperRule);
            } else {
              list.insertData(wrapperRule, item.next);
            }
            list.remove(item);  // remove original at rule
            selectorStack.push([]);   // push an empty array to be popped when we leave
            return;
          }

          // we should have either one media query or none of them needs to be replaced
          const selectors = flatten(mediaQueries.map(replaceMediaFeaturesAndYieldSelectors));
          selectorStack.push(selectors);
          break;
        }
        case 'Rule': {
          if (
            selectorStack.length === 0 || // we aren't in a media rule
            selectorStack.every(layer => layer.length === 0) // or we're in a media rule with no rewritten selectors
          ) {
            break;
          }

          if (node.prelude.type !== 'SelectorList') {  // or somehow our rule's prelude is not SelectorList (alternative is Raw)
            console.warn('Failed to prepend media query selectors for @media replacement because the rule prelude is type Raw; playback may be affected.');
            break;
          }

          // the goal here is to duplicate this rules block per split of media query list
          // and prefix each selector prelude with the selectors in the selectorStack
          const selectorsToPrepend = flatten(selectorStack).reverse(); // prepend LIFO

          for (const selector of selectorsToPrepend) {
            prependSelector(node.prelude, selector);
          }
          break;
        }
      }
    },
    leave(node: CssNode) {
      if (node.type === 'Atrule' && node.name === 'media' && node.prelude?.type === 'AtrulePrelude') {
        selectorStack.pop();
      }
    },
  };
}

function prependSelector(selectorList: SelectorList, selectorToPrepend: MediaRewriteSelector) {
  selectorList.children.each(child => {
    if (child.type !== 'Selector') {
      return;
    }

    if (isSelectorExplicitlyReferencingRoot(child)) {
      if (!child.children.first()) {
        return;
      }
      // Prepend the new selector without whitespace when the original selector explicitly references the root.
      const firstGrandchild = child.children.shift();
      child.children.prependData(selectorToPrepend);
      child.children.prependData(firstGrandchild!.data);
    } else {
      // Clone the original selector and prepend the new one without whitespace (a descendant combinator).
      // This handles an edge case where the original selector implicitly references the root.
      const cloned = clone(child) as Selector;
      cloned.children.prependData(selectorToPrepend);
      cloned.children.prependData(createPseudoClassSelector('root', selectorList.loc));
      selectorList.children.prependData(cloned);

      // Prepend the new selector with whitespace (a descendant combinator).
      // This handles normal cases where the original selector references a descendant of the root.
      child.children.prependData(whiteSpace);
      child.children.prependData(selectorToPrepend);
      child.children.prependData(createPseudoClassSelector('root', selectorList.loc));
    }
  });

  const mutated = selectorList as Mutated<SelectorList>;
  mutated[mutatedTag] = true;
}

function isSelectorExplicitlyReferencingRoot(selector: SelectorList | Selector): boolean {
  const firstChild = selector.children.first();
  switch (firstChild?.type) {
    case 'PseudoClassSelector':
      return firstChild.name === 'root';
    case 'Selector':
      return isSelectorExplicitlyReferencingRoot(firstChild);
    case 'TypeSelector':
      return firstChild.name.toLowerCase() === 'html' || firstChild.name.toLowerCase() === 'svg';
  }

  return false;
}

/**
 * Given an @media prelude, return an array of all media queries that are immediate descendents of this rule
 * Each generated item is [MediaQuery, List]
 * @param prelude @media prelude
 */
function getMediaQueriesFromPrelude(prelude: AtrulePrelude): MediaQuery[] {
  const queriesList: MediaQuery[] = [];
  // Note: all media prelude seems to have a child of MediaQueryList, even if there are multiple MediaQuery
  prelude.children
    .each(mediaQueryList => {
      if (mediaQueryList.type !== 'MediaQueryList') {
        return;
      }
      mediaQueryList.children
        .each(mediaQuery => {
          if (mediaQuery.type !== 'MediaQuery') {
            return;
          }
          queriesList.push(mediaQuery);
        });
    });

  return queriesList;
}

function shouldReplaceMediaQueryChild(child: CssNode): boolean {
  return child.type === 'MediaFeature' && Object.hasOwnProperty.call(mediaFeaturesToBeReplaced, child.name);
}

/**
 * Given a media query, replace all media features that are keyed in `mediaFeaturesToBeReplaced`
 * with a selector that replaces it
 * @param mediaQueryInfo MediaQuery to look in
 */
function replaceMediaFeaturesAndYieldSelectors(mediaQuery: MediaQuery): MediaRewriteSelector[] {
  const selectorList: MediaRewriteSelector[] = [];
  const queryFirstChild = mediaQuery.children.first();
  const queryNegated = queryFirstChild?.type === 'Identifier' && queryFirstChild.name?.toLowerCase() === 'not';

  mediaQuery.children.each((child, item, list) => {
    if (child.type !== 'MediaFeature' || !shouldReplaceMediaQueryChild(child)) {
      return;
    }

    const featureNegated = getNegation(item, queryFirstChild);
    const negateSelector = queryNegated ? !featureNegated : featureNegated;

    const method = mediaFeaturesToBeReplaced[child.name]!;
    const baseSelector: MediaRewriteSelector = method.call(null, child);
    const selector = negateSelector ? createNegation(baseSelector) : baseSelector;
    selectorList.push(selector);

    replaceMediaFeatureWithConstant(child.loc, child, !featureNegated);
  });

  return selectorList;
}

function replaceMediaFeatureWithConstant(loc: CssLocation | undefined, mediaFeature: MediaFeature, isTrue: boolean) {
  // NOTE (eugenechang) this replaces target media feature with either "true" or "false"
  // true is represented as (min-width: 0) which is always true
  // false is represented as (max-width: 0) which is always false
  mediaFeature.name = isTrue ? 'min-width' : 'max-width';
  mediaFeature.value = createDimension(loc, '0');
  mediaFeature.loc = loc;
  (mediaFeature as Mutated<MediaFeature>)[mutatedTag] = true;
}

const whiteSpace: WhiteSpace = {
  type: 'WhiteSpace',
  value: ' ',
};

/**
 * Returns true iff this item is preceeded by an odd number of negation operator 'not', NOT including the first child of the list
 * This function only iterates through identifier and whitespaces prior to given item, and returns early if preceded by anything else
 */
function getNegation(item: ListItem<CssNode> | null, firstItem: CssNode | null) {
  let negationCount = 0;
  itemLoop: for (let current = item?.prev; current && current.data !== firstItem; current = current.prev) {
    switch (current.data.type) {
      case 'Identifier':
        if (current.data.name?.toLowerCase() === 'not') {
          negationCount++;
        }
        break;
      case 'WhiteSpace':
        break;
      default:
        break itemLoop;
    }
  }

  return negationCount % 2 === 1;
}
